import { translateBlock } from 'i18n';
import _ from 'lodash';
import numbro from 'numbro';
import {
    CHAPTER_LABEL_FIELD,
    CHAPTER_FIELD,
    PRIMITIVE_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    BUDGET_BALANCE_TOTAL_FIELD,
} from 'routes/BudgetModule/config/constants';
import { BudgetLine, BudgetProposalT } from 'routes/BudgetModule/config/types';
import { BudgetPhases } from 'business/constants';
import { getEarliestActivePhase } from 'routes/BudgetModule/shared/phases/helpers';
import { INDICATORS_FILTERS, BALANCE_METRICS } from './constants';
import { getClientBind } from 'store/APIClient';

/**
 * HELPERS TO AGGREGATE DATA FROM DIFFERENT QUERIES
 */

const sortByChapter = (a: ResponseDataT, b: ResponseDataT): number => {
    // @ts-expect-error [TS migration] Was not detected by flow
    const valueA = parseInt(a[CHAPTER_FIELD], 10);
    // @ts-expect-error [TS migration] Was not detected by flow
    const valueB = parseInt(b[CHAPTER_FIELD], 10);
    return valueA - valueB;
};

/**
 * HELPERS FOR INDICATORS DATA
 */

export const getAmountsByChapters = (
    data: ResponseDataT[],
): ResponseDataT[] => {
    const result: ResponseDataT[] = [];

    const grouped = _.groupBy(data, (row) => row[CHAPTER_LABEL_FIELD]);

    _.map(grouped, (chapterData, chapter) => {
        const chapterTotals: ResponseDataT = {};
        BALANCE_METRICS.forEach((metric) => {
            chapterTotals[metric] =
                _.sumBy(chapterData, (row) => (row[metric] as number) || 0) ||
                0;
        });
        chapterTotals[CHAPTER_LABEL_FIELD] = chapter;
        chapterTotals[CHAPTER_FIELD] = chapterData[0][CHAPTER_FIELD];
        result.push(chapterTotals);
    });
    return result.sort(sortByChapter);
};

export type IndicatorFilterRow = Pick<
    BudgetLine,
    'chapter.code' | 'nature.number'
>;

export const getIndicatorsData = (
    data: ResponseDataT[],
    indicators: string[],
): ResponseDataT[] => {
    const result: ResponseDataT[] = [];
    indicators.forEach((indicator) => {
        const filteredData = data.filter((row: IndicatorFilterRow) =>
            INDICATORS_FILTERS[indicator](row),
        );
        const indicatorTotals: ResponseDataT = {};
        BALANCE_METRICS.forEach((metric) => {
            indicatorTotals[metric] =
                // @ts-expect-error [TS migration] Was not detected by flow
                _.sumBy(filteredData, (row) => row[metric] || 0) || 0;
        });
        indicatorTotals[CHAPTER_LABEL_FIELD] = translateBlock(indicator);
        result.push(indicatorTotals);
    });
    return result;
};

export const getMetricsForFinalIndicator = (
    budgetProposals: BudgetProposalT[],
): string[] => {
    const allPhases = budgetProposals.map((bp) => bp.phase);
    const earliestPhase = getEarliestActivePhase(allPhases);

    // (Shortcut ID : 30915) : add amounts for ampmetropole
    const bind = getClientBind();

    switch (earliestPhase) {
        case BudgetPhases.PROPOSITION_SERVICES:
            if (bind === 'ampmetropole')
                // (Shortcut ID : 30915) : add amounts for ampmetropole
                return [
                    SERVICE_PROPOSAL_AMOUNT,
                    PRIMITIVE_BUDGET_AMOUNT,
                    SUPPLEMENTARY_BUDGET_AMOUNT,
                ];
            return [SERVICE_PROPOSAL_AMOUNT];
        case BudgetPhases.ARBITRAGE:
            if (bind === 'ampmetropole')
                // (Shortcut ID : 30915) : add amounts for ampmetropole
                return [
                    SIMULATED_ARBITRATED_AMOUNT,
                    PRIMITIVE_BUDGET_AMOUNT,
                    SUPPLEMENTARY_BUDGET_AMOUNT,
                ];
            return [SIMULATED_ARBITRATED_AMOUNT];
        case BudgetPhases.VOTE:
            if (bind === 'ampmetropole')
                // (Shortcut ID : 30915) : add amounts for ampmetropole
                return [PRIMITIVE_BUDGET_AMOUNT, SUPPLEMENTARY_BUDGET_AMOUNT];
            return [PRIMITIVE_BUDGET_AMOUNT];
        case BudgetPhases.SUPPLEMENTARY_BUDGET:
            return [PRIMITIVE_BUDGET_AMOUNT, SUPPLEMENTARY_BUDGET_AMOUNT];

        default:
            throw new Error(
                `Cannot get metrics for unknown phase ${earliestPhase}`,
            );
    }
};

export const getFinalIndicatorColumnField = (metrics: string[]): string => {
    if (metrics.length === 1) {
        return metrics[0];
    }
    return BUDGET_BALANCE_TOTAL_FIELD;
};

const getFinalIndicator = (
    data: ResponseDataT[],
    metrics: string[],
): number => {
    let result = 0;
    metrics.forEach((metric) => {
        // @ts-expect-error [TS migration] Was not detected by flow
        result += _.sumBy(data, (row) => row[metric] || 0) || 0;
    });

    return result;
};

export const getFieldWithReportFromField = (field: string): string =>
    `${field}WithReport`;

const getFinalIndicatorWithReportColumnField = (metrics: string[]): string => {
    const finalIndicatorColumnField = getFinalIndicatorColumnField(metrics);
    return getFieldWithReportFromField(finalIndicatorColumnField);
};

const getFinalIndicatorWithReport = (
    data: ResponseDataT[],
    metrics: string[],
): number =>
    getFinalIndicator(data, [getFinalIndicatorWithReportColumnField(metrics)]);

export const getFinalIndicatorFormatted = (
    data: ResponseDataT[],
    metrics: string[],
): string => {
    const finalAmount = getFinalIndicator(data, metrics);
    return numbro(finalAmount).formatCurrency({
        mantissa: 2,
        trimMantissa: true,
        spaceSeparated: true,
        thousandSeparated: true,
    });
};

export const getFinalIndicatorWithReportFormatted = (
    data: ResponseDataT[],
    metrics: string[],
): string => {
    const finalAmount = getFinalIndicatorWithReport(data, metrics);
    return numbro(finalAmount).formatCurrency({
        mantissa: 2,
        trimMantissa: true,
        spaceSeparated: true,
        thousandSeparated: true,
    });
};

export const getHighlightedMetric = (
    finalIndicatorColumnField: string,
    isReportedAmountUsed: boolean,
): string =>
    isReportedAmountUsed
        ? getFieldWithReportFromField(finalIndicatorColumnField)
        : finalIndicatorColumnField;
